import { AppContext } from "contexts/AppContextProvider";
import React, { useEffect, useState } from "react";
import Editor from "./editor/Editor";
import { debounce } from "lodash";
import { ApolloProvider, useMutation } from "@apollo/client";
import { CREATE_NOTE, UPDATE_NOTE } from "./editor/graphql/EditorGraphQL";
import client from "client";

import clsx from "clsx";

/**
 * Text editor widget that allows users to add key findings (notes).
 * @returns
 */
const KeyFindingsEditor = (props) => {
  const { currentProject } = React.useContext(AppContext).state;

  const [note, setNote] = useState(
    // get the last note
    currentProject.boardObj.notes[
      currentProject.boardObj.notes.sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
      ).length - 1
    ]
  );

  useEffect(() => {
    setNote(
      currentProject.boardObj.notes[
        currentProject.boardObj.notes.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        ).length - 1
      ]
    );
  }, [currentProject]);
  const [createNote, { loading: isSavingCreate }] = useMutation(CREATE_NOTE);
  const [updateNote, { loading: isSavingUpdate }] = useMutation(UPDATE_NOTE);

  const [showLoading, setShowLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState(null);
  const [shouldSave, setShouldSave] = useState(false);

  useEffect(() => {
    debounce(() => setShowLoading(false), 3500)();
  }, [showLoading]);

  useEffect(() => {
    if (isSavingCreate || isSavingUpdate) {
      setShowLoading(true);
    }
  }, [isSavingCreate, isSavingUpdate]);

  const onNoteSaved = (data) => {
    setNote(data.data.boardNoteCreate || data.data.boardNoteUpdate);
    setIsEditing(false);
    setShouldSave(false);
  };

  const onChangeNote = (content) => {
    setContent(content);
    // when the change on the state was made.. notify the user that he should/could save the changes
    if (
      note &&
      JSON.stringify(content) !== note.body &&
      isEditing
      // or, there's no note yet but a new text was inserted to be CREATED
    ) {
      setShouldSave(true);
    }
  };

  /**
   * Calls the endpoint to create or update the note
   * @param {*} newContent
   */
  const persistNote = (newContent) => {
    // in the case the note doesn't exist.. create a new one
    if (!note) {
      createNote({
        variables: {
          boardID: currentProject.boardObj.id,
          title: "Key Findings",
          body: JSON.stringify(newContent),
        }, // when the mutation is concluded, disable few controls
      }).then(onNoteSaved);
    }
    // in the case the note exist and changes were made, just update it
    else if (JSON.stringify(newContent) !== note.body) {
      updateNote({
        variables: {
          id: note.id,
          title: "Key Findings",
          body: JSON.stringify(newContent),
        }, // when the mutation is concluded, disable few controls
      }).then(onNoteSaved);
    } else {
      setIsEditing(false);
    }
  };

  // just to make the flow more fancy
  const onChangeNoteDebounce = onChangeNote;

  // Get permissions from localStorage
  const permissions = localStorage.permissions
    ? JSON.parse(localStorage.permissions)
    : [];

  // does the user has permission to edit it?
  const canEdit =
    permissions.find(
      (permission) =>
        permission.action === "*" && permission.object === "board_note"
    ) !== undefined;

  return (
    <>
      <div className="editor">
        <div className="border">
          {showLoading && (
            <div>
              <div className={"editor-circle animated-label"}>
                <div className="circle-scale circle-scale--sm" />
              </div>
              <div className="editor-label">
                <div className={"animated-label"}>
                  <span className="saved-label">Saving</span>
                </div>
              </div>
            </div>
          )}

          <Editor
            onChangeNote={onChangeNoteDebounce}
            note={note}
            // only edit it in the case the user can edit and the editor is on edit mode
            editable={canEdit && isEditing}
          />
        </div>
        {/* only allow to edit whose that has permission */}
        {canEdit && (
          <div className="mt-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                isEditing ? persistNote(content) : setIsEditing(true);
              }}
              className={clsx("edit-button", {
                "is-editing": isEditing,
              })}
            >
              {isEditing ? "Save" : "Edit"}
            </button>

            <div
              className={clsx("d-inline-block ml-2", {
                "should-save": shouldSave,
                "should-not-save": !shouldSave,
              })}
            >
              You have unsaved changes
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const KeyFindings = (props) => (
  <ApolloProvider client={client}>
    <KeyFindingsEditor {...props} />
  </ApolloProvider>
);

export default KeyFindings;
